import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessengerService, Str, __, randomString } from '@web/birdz-angular';
import { KpiFavorite, isKpiFavorite } from 'app/interfaces/kpi-favorite.interface';
import { UserService } from 'app/services/user.service';
import { EMPTY, Observable, Subject, defer, filter, finalize, map, switchMap, tap } from 'rxjs';

/** the key the favorites are stored under in the user's preferences */
export const KPI_FAVORITES_PREFERENCE_KEY = 'supervision_kpi_selection_favorites';
export const MAX_KPI_FAVORITES = 100;
export const MAX_KPIS_PER_FAVORITE = 100;
export const MAX_KPI_FAVORITE_LABEL_LENGTH = 128;

/**
 * State and persistence of the KPI selection favorites, stored in the user's preferences.
 *
 * A favorite holds the KPI ids of a selection. The two controls of the feature sit at
 * different places in the toolbar and share this state : KpiFavoriteSaveComponent (the star)
 * saves the current selection, KpiFavoritesMenuComponent applies / renames / deletes an
 * existing one.
 *
 * Provide it on the component that hosts both controls, not in root : the state is tied to
 * one KPI selection.
 */
@Injectable()
export class KpiFavoritesService {
  private userService = inject(UserService);
  private messenger = inject(MessengerService);
  private destroyRef = inject(DestroyRef);

  favorites: KpiFavorite[] = [];
  currentFavorite: KpiFavorite | null = null;
  isSaving = 0;

  /** currently selected KPI ids, pushed by the host component through syncSelection */
  selection: string[] = [];
  /** KPI ids available for the current contract, used to ignore obsolete ids stored in a favorite */
  availableKpiIds: string[] = [];
  /** the same ids, for the membership tests isApplicable() runs on every change detection cycle */
  private availableKpiIdSet = new Set<string>();

  /** emits the KPI ids to apply when a favorite is selected in the menu */
  private favoriteApplied = new Subject<string[]>();
  readonly favoriteApplied$ = this.favoriteApplied.asObservable();

  constructor() {
    this.setFavorites(
      (this.userService.getUser()?.preferences?.[KPI_FAVORITES_PREFERENCE_KEY] ?? []).filter(isKpiFavorite)
    );
  }

  /** the favorites are held sorted by label, which is the order the menu lists them in */
  setFavorites(favorites: KpiFavorite[]): void {
    this.favorites = [...favorites].sort(Str.sortBy('label'));
  }

  /**
   * Takes the current selection into account, refreshing the matched favorite.
   *
   * To be called by the host component on every change of its selection, including the
   * programmatic ones : the selection array can't be watched from here, as the select's
   * search bar mutates it in place when a KPI is checked instead of assigning a new one
   * (see SelectSearchBarComponent.onMultipleSelectSelectionChange), which no input binding
   * nor reference comparison would catch.
   */
  syncSelection(selection: string[], availableKpiIds: string[]): void {
    this.selection = selection;
    this.availableKpiIds = availableKpiIds;
    this.availableKpiIdSet = new Set(availableKpiIds);

    this.refreshCurrentFavorite();
  }

  /** the KPI ids of a favorite which still exist for the current contract */
  getApplicableKpiIds(favorite: KpiFavorite): string[] {
    return this.availableKpiIds.filter((id) => favorite.kpis.includes(id));
  }

  /**
   * Whether at least one KPI of the favorite exists for the current contract.
   *
   * The menu calls it for each of its entries on every change detection cycle, even closed,
   * so it stops at the first match instead of building the list getApplicableKpiIds() would.
   */
  isApplicable(favorite: KpiFavorite): boolean {
    return favorite.kpis.some((id) => this.availableKpiIdSet.has(id));
  }

  /** whether the current selection is exactly the one of the given favorite */
  matchesSelection(favorite: KpiFavorite): boolean {
    const kpiIds = this.getApplicableKpiIds(favorite);

    // a favorite holding none of the contract's KPIs would otherwise match an empty selection
    if (kpiIds.length === 0) {
      return false;
    }

    return kpiIds.length === this.selection.length && kpiIds.every((id) => this.selection.includes(id));
  }

  refreshCurrentFavorite(): void {
    this.currentFavorite = this.favorites.find((favorite) => this.matchesSelection(favorite)) ?? null;
  }

  /**
   * Whether a write is in flight. All three operations rebuild the whole list from
   * `this.favorites` and the backend merges it as a whole, so two concurrent writes would
   * make the last one silently drop what the first did.
   */
  isWriting(): boolean {
    return this.isSaving > 0;
  }

  /** whether there is a selection to save, whether it already is a favorite or not */
  isSaveAvailable(): boolean {
    return !this.isWriting() && this.selection.length > 0;
  }

  /** the star is disabled when there is nothing to save or when the selection is already a favorite */
  isSaveDisabled(): boolean {
    return !this.isSaveAvailable() || this.currentFavorite !== null;
  }

  saveFavorite(): void {
    // the star is kept interactive while disabled so that it can show its tooltip,
    // so the click has to be discarded here
    if (this.isSaveDisabled()) {
      return;
    }

    if (this.favorites.length >= MAX_KPI_FAVORITES) {
      this.messenger.error(__('MAX_FAVORITES_REACHED', { max: MAX_KPI_FAVORITES }));
      return;
    }

    if (this.selection.length > MAX_KPIS_PER_FAVORITE) {
      this.messenger.error(__('MAX_KPIS_PER_FAVORITE_REACHED', { max: MAX_KPIS_PER_FAVORITE }));
      return;
    }

    this.promptLabel()
      .pipe(
        switchMap((label) => {
          const favorite: KpiFavorite = { id: randomString(10), label, kpis: [...this.selection] };
          return this.persistAll([...this.favorites, favorite]);
        }),
        tap(() => this.messenger.success(__('FAVORITE_SAVED')))
      )
      .subscribe();
  }

  applyFavorite(favorite: KpiFavorite): void {
    // applying it would empty the selection without the favorite being applied in any way
    if (!this.isApplicable(favorite)) {
      this.messenger.error(__('FAVORITE_HAS_NO_AVAILABLE_KPI'));
      return;
    }

    this.currentFavorite = favorite;
    this.favoriteApplied.next(this.getApplicableKpiIds(favorite));
  }

  editFavorite(favorite: KpiFavorite, event: MouseEvent): void {
    event.stopPropagation();

    if (this.isWriting()) {
      return;
    }

    this.promptLabel(favorite)
      .pipe(
        // the name was left as it was : nothing to save, and nothing to report
        filter((label) => label !== favorite.label),
        switchMap((label) => this.persistAll(this.favorites.map((f) => (f.id === favorite.id ? { ...f, label } : f)))),
        tap(() => this.messenger.success(__('FAVORITE_EDITED')))
      )
      .subscribe();
  }

  deleteFavorite(favorite: KpiFavorite, event: MouseEvent): void {
    event.stopPropagation();

    if (this.isWriting()) {
      return;
    }

    this.messenger
      .confirm(__('CONFIRM_FAVORITE_DELETION', { label: favorite.label }))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((confirmed) => confirmed),
        switchMap(() => this.persistAll(this.favorites.filter((f) => f.id !== favorite.id))),
        tap(() => this.messenger.success(__('FAVORITE_REMOVED')))
      )
      .subscribe();
  }

  /**
   * Asks for a favorite name, rejecting empty names and names already taken
   * (the unicity check is case insensitive)
   */
  promptLabel(favorite?: KpiFavorite): Observable<string> {
    return this.messenger
      .prompt(__('ENTER_FAVORITE_NAME'), __('FAVORITE_NAME'), {
        maxLength: MAX_KPI_FAVORITE_LABEL_LENGTH,
        answer: favorite?.label ?? '',
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((label): label is string => label !== undefined),
        map((label) => label.trim()),
        filter((label) => label.length > 0),
        filter((label) => {
          const isTaken = this.favorites.some(
            (f) => f.id !== favorite?.id && f.label.localeCompare(label, undefined, { sensitivity: 'accent' }) === 0
          );
          if (isTaken) {
            this.messenger.error(__('FAVORITE_NAME_ALREADY_TAKEN'));
          }
          return !isTaken;
        })
      );
  }

  /**
   * Overwrites the user's stored favorites with the given list — callers pass the whole list,
   * not a change to it — then takes the stored state back from the answer : `favorites` and
   * `currentFavorite` are refreshed from what the backend actually kept.
   *
   * On failure the stream completes without emitting : the friendly message has been shown,
   * the callers' success message is skipped, and none of their bare subscribe() gets an
   * unhandled error.
   *
   * The counter is raised on subscription, not on call : an observable built but never
   * subscribed would otherwise leave it raised for good, freezing every control which reads
   * isWriting().
   */
  persistAll(favorites: KpiFavorite[]): Observable<unknown> {
    return defer(() => {
      this.isSaving++;
      return this.userService.patchPreferences({ [KPI_FAVORITES_PREFERENCE_KEY]: favorites });
    }).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap((preferences) => {
        this.setFavorites(
          ((preferences as Record<string, unknown>)?.[KPI_FAVORITES_PREFERENCE_KEY] ?? []) as KpiFavorite[]
        );
        this.refreshCurrentFavorite();
      }),
      this.messenger.showFriendlyErrorMessage(EMPTY),
      finalize(() => this.isSaving--)
    );
  }
}
